import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { locale as navigationEnglish } from 'app/navigation/i18n/en';
import { locale as navigationItalian } from 'app/navigation/i18n/it';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FlamingoAuthService } from '../@flamingo/service/flamingo-auth.service';
import { UserType } from './core/model/user.model';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  fuseConfig: any;
  navigation: any;
  lang: string;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {DOCUMENT} document
   * @param {FlamingoAuthService} _authService
   * @param {Router} _router
   * @param {FuseConfigService} _fuseConfigService
   * @param {FuseSidebarService} _fuseSidebarService
   * @param {FuseSplashScreenService} _fuseSplashScreenService
   * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
   * @param {Platform} _platform
   * @param {TranslateService} _translateService
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private _authService: FlamingoAuthService,
    private _router: Router,
    private _fuseConfigService: FuseConfigService,
    private _fuseSidebarService: FuseSidebarService,
    private _fuseSplashScreenService: FuseSplashScreenService,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private _translateService: TranslateService,
    private _platform: Platform,
  ) {
    const languages = ['it', 'en'];
    const defaultLang = 'en';

    // Get default navigation
    // this.navigation = navigation;
    // Register the navigation to the service
    // this._fuseNavigationService.register('main', this.navigation);
    // Set the main navigation as our current navigation
    // this._fuseNavigationService.setCurrentNavigation('main');

    // Add languages
    this._translateService.addLangs(languages);

    // Set the default language
    this._translateService.setDefaultLang(defaultLang);

    // Set the navigation translations
    this._fuseTranslationLoaderService.loadTranslations(navigationItalian, navigationEnglish);

    // Use a language
    if (localStorage.length > 0) {
      this.lang = localStorage.getItem('lang');
    }

    if (this.lang) {
      this._translateService.use(this.lang);
    } else {
      this._translateService.use(
        languages.find(l => l === navigator.language.split('-')[0])
          ? languages.find(l => l === navigator.language.split('-')[0])
          : defaultLang,
      );
    }

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix Start
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     * If you are using a language other than the default one, i.e. Turkish in this case,
     * you may encounter an issue where some of the components are not actually being
     * translated when your app first initialized.
     *
     * This is related to ngxTranslate module and below there is a temporary fix while we
     * are moving the multi language implementation over to the Angular's core language
     * service.
     **/

    // Set the default language to 'en' and then back to 'tr'.
    // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
    // been selected and there is no way to force it, so we overcome the issue by switching
    // the default language back and forth.
    setTimeout(() => {
      this._translateService.setDefaultLang('en');
      this._translateService.setDefaultLang('it');
    });

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix End
     * ----------------------------------------------------------------------------------------------------
     */

    // Add is-mobile class to the body if the platform is mobile
    if (this._platform.ANDROID || this._platform.IOS) {
      this.document.body.classList.add('is-mobile');
    }

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._authService.sessionEnded$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(userType => {
        if (this._router.url.includes('/auth')) {
          return;
        }

        switch (userType) {
          case UserType.buyer:
            this._router.navigate(['/buyer/auth']);
            break;
          case UserType.partner:
          case UserType.partnerBranchMaster:
          case UserType.partnerAgent:
            this._router.navigate(['/partner/auth']);
            break;
          default:
            this._router.navigate(['/auth']);
            break;
        }
      });

    // set the theme based on the user
    if (this._authService.isLogged()) {
      let theme = 'theme-cashme-light';
      // if (this._authService.user.type === 'SELLER') {
      //   theme = 'theme-cashMe-seller-pink-light';
      //   this._titleService.setTitle('CashMe App - Seller');
      // } else if (this._authService.user.type === 'BUYER') {
      //   theme = 'theme-cashMe-buyer-blue-light';
      //   this._titleService.setTitle('CashMe App - Buyer');
      // }
      this._fuseConfigService.config = {
        colorTheme: theme,
      };
    }

    // Subscribe to config changes
    this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.fuseConfig = config;

      // Boxed
      if (this.fuseConfig.layout.width === 'boxed') {
        this.document.body.classList.add('boxed');
      } else {
        this.document.body.classList.remove('boxed');
      }

      // Color theme - Use normal for loop for IE11 compatibility
      for (let i = 0; i < this.document.body.classList.length; i++) {
        const className = this.document.body.classList[i];

        if (className.startsWith('theme-')) {
          this.document.body.classList.remove(className);
        }
      }

      this.document.body.classList.add(this.fuseConfig.colorTheme);
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebarOpen(key): void {
    this._fuseSidebarService.getSidebar(key).toggleOpen();
  }
}
